import React from "react";
import { useEffect, useMemo, useState } from "react";
import {
    IStackStyles,
    Stack,
    Link
} from "@fluentui/react";
import { CustomFontText } from "components/customFontText/CustomFontText";
import { Col, Row } from "react-grid-system";
import {
    useSelector,
    useDispatch
} from "react-redux";
import { AppState } from "store/index";
import { isKarlProfile } from "utils/permissions/permissionsHelpers";
import { handleGetLocations } from "store/locations/locations.action";
import { handleGetCarlReport, handlePrintCarlReport } from "store/dealTracker/dealTracker.action";
import { TreeTable } from 'primereact/treetable';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown as PRDropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { classNames } from "primereact/utils";
import { currencyNegValFractionalFormat, numberFormat } from "utils/helperFunctions";
import moment from "moment";
import { carlReportNewUsedOptions } from "constants/constants";

const container: IStackStyles = {
    root: {
        marginTop: 10,
        minHeight: 500
    },
};



// Report data only goes back to May 2022, so the Select Month dropdown
// offers every month from then through the current month, newest first.
const getMonthYearOptions = () => {
    const options: { key: string; text: string }[] = [];
    const start = moment('2022-05-01');
    const cursor = moment().startOf('month');
    while (cursor.isSameOrAfter(start, 'month')) {
        options.push({ key: `${cursor.year()}-${cursor.month() + 1}`, text: cursor.format('MMMM YYYY') });
        cursor.subtract(1, 'month');
    }
    return options;
};

const metricColumns: { field: string; header: string; type: 'number' | 'currency' | 'percent' }[] = [
    { field: 'goal', header: 'Goal', type: 'number' },
    { field: 'lastMonth', header: 'Last Month', type: 'number' },
    { field: 'threeMoAvg', header: '3 Mo Avg', type: 'number' },
    { field: 'twelveMoAvg', header: '12 Mo Avg', type: 'number' },
    { field: 'appts', header: 'Appts', type: 'number' },
    { field: 'avgGross', header: 'Avg Gross', type: 'currency' },
    { field: 'closed', header: 'Closed', type: 'number' },
    { field: 'mtdVsGoal', header: 'MTD vs Goal', type: 'percent' },
    { field: 'mtdWritten', header: 'MTD Written', type: 'number' },
    { field: 'pending', header: 'Pending', type: 'number' },
    { field: 'mtdOrderOut', header: 'MTD Order Out', type: 'number' },
];

const metricColumnWidth = (type: string) => (type === 'currency' ? '8.5%' : '6.3%');

// Carl Report API field names differ from the internal metricColumns fields.
const mapApiMetrics = (src: any) => {
    if (!src) {
        return {};
    }
    return {
        goal: src.goal,
        lastMonth: src.lastMonth,
        threeMoAvg: src.threeMonthAvg,
        twelveMoAvg: src.twelveMonthAvg,
        appts: src.appointments,
        avgGross: src.avgGross,
        closed: src.closed,
        mtdVsGoal: src.mtdVsGoal,
        mtdWritten: src.written,
        pending: src.pending,
        mtdOrderOut: src.orderOut,
    };
};

const formatMetricValue = (type: string, val: any) => {
    if (val === undefined || val === null || val === '') {
        return '';
    }
    if (type === 'currency') {
        return currencyNegValFractionalFormat(val);
    }
    if (type === 'percent') {
        return `${val}`;
    }
    return numberFormat(val);
};

// Sizes every column in an exported worksheet to fit its widest cell,
// so the Excel output doesn't need a manually tuned column width per report.
const autoFitExcelColumns = (worksheet: any) => {
    const colWidths: number[] = [];
    worksheet.eachRow({ includeEmpty: true }, (row: any) => {
        row.eachCell({ includeEmpty: true }, (cell: any, colNumber: number) => {
            const cellText = cell.value === undefined || cell.value === null ? '' : String(cell.value);
            colWidths[colNumber] = Math.max(colWidths[colNumber] || 10, cellText.length + 2);
        });
    });
    colWidths.forEach((width, colNumber) => {
        if (colNumber) {
            worksheet.getColumn(colNumber).width = width;
        }
    });
};

// Matches the light red/light green MTD vs Goal highlighting from the
// production Excel export: behind or at goal is red, ahead of goal is green.
const fillMtdVsGoalCell = (cell: any, val: any) => {
    if (typeof val !== 'number') {
        return;
    }
    cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: val > 0 ? 'FFEAF1E0' : 'FFF2DCDB' },
    };
};

// Light green for positive MTD vs Goal, light red otherwise (same as the Excel fill).
const mtdVsGoalCellClass = (col: { field: string }, val: any) => {
    if (col.field !== 'mtdVsGoal' || typeof val !== 'number') {
        return '';
    }
    return val > 0 ? 'carlMtdPositive' : 'carlMtdNegative';
};

// Selling days follow the dealership convention of excluding Sundays.
const getSellingDaysInfo = (month: number, year: number) => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const today = new Date();
    let sellingDays = 0;
    let daysPast = 0;
    for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, month - 1, d);
        if (date.getDay() !== 0) {
            sellingDays++;
            if (date <= today) {
                daysPast++;
            }
        }
    }
    return { sellingDays, daysPast, daysRemaining: sellingDays - daysPast };
};

export const CarlNewReport = () => {
    const dispatch = useDispatch();
    const ExcelJS = require('exceljs');
    const { saveAs } = require('file-saver');

    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    const monthYearOptions = useMemo(() => getMonthYearOptions(), []);

    const user = useSelector((state: AppState) => state.userProfile.userProfile) as any;
    const locations = useSelector((state: AppState) => state.locations.locations) as any;
    const isKarlProfileType = user.profileType && isKarlProfile(user.profileType);
    const [locationList, setLocationList] = useState([]);
    const [monthYearFilter, setMonthYearFilter] = useState(`${currentYear}-${currentMonth}`);
    const [locationFilter, setLocationFilter] = useState(user?.primaryLocation?.id ?? locationList[0]?.id ?? 0);
    const [conditionFilter, setConditionFilter] = useState<string[]>(['New', 'Used']);

    const [carlReportData, setCarlReportData] = useState<any>(null);
    const [commonServerError, setcommonServerError] = useState(null);
    const [expandedKeys, setExpandedKeys] = useState<any>({});

    const [selectedYear, selectedMonth] = monthYearFilter.split('-').map(Number);

    useEffect(() => {
        dispatch<any>(handleGetLocations());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (locations && locations.results && locations.results.length) {
            const list = [...locations.results];
            setLocationList(list);
            setLocationFilter(user?.primaryLocation?.id ?? list[0]?.id ?? 0);
        }
    }, [locations,user?.primaryLocation?.id]);

    // GET /reports/carlReport?month=&year=&primaryLocationId=&newUsed=
    useEffect(() => {
        if(locationFilter && conditionFilter.length) {
               const formData: any = {
            month: selectedMonth,
            year: selectedYear,
        };
        if (locationFilter) {
            formData.primaryLocationId = locationFilter;
        }
        if (conditionFilter.length) {
            formData.newUsed = conditionFilter.map((v) => v.toLowerCase()).join(',');
        }
        setcommonServerError(null);
        dispatch<any>(handleGetCarlReport({
            formData,
            callback: (res) => {
                if (res) {
                    setCarlReportData(res);
                } else {
                    setCarlReportData(null);
                    setcommonServerError('Failed to Fetch the Data.');
                }
            }
        }));
        }else{
            setCarlReportData(null);
            setcommonServerError(null);
        }
     
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [monthYearFilter, locationFilter, conditionFilter]);

    const onMonthChange = (e) => setMonthYearFilter(e.value);
    const onLocationChange = (e) => setLocationFilter(e.value);
    const onConditionChange = (e) => setConditionFilter(e.value);

    const locationTotalsData = useMemo(() => {
        // The endpoint returns a single totals object for the requested
        // location (or the user's primary location when none is selected).
        if (carlReportData && carlReportData.totals) {
            return [{
                id: locationFilter || 0,
                locationName: carlReportData.totals.locationName,
                ...mapApiMetrics(carlReportData.totals),
            }];
        }
        // No totals from the API for this selection — the DataTable shows
        // its default "no records" empty state.
        return [];
    }, [carlReportData, locationFilter]);

    const managerTreeData = useMemo(() => {
        // Prefer the live Carl Report API response once it returns data.
        // Manager-level metrics are nested under `totals`; each Sales
        // Person's metrics are flat fields on the salesPersons entry.
        if (carlReportData && Array.isArray(carlReportData.details) && carlReportData.details.length) {
            return carlReportData.details.map((mgr: any, index) => {
                const children = (mgr.salesPersons || []).map((emp: any, i) => ({
                    key: `${index}-${i}`,
                    data: {
                        manager: '',
                        salesPerson: `${emp.firstName} ${emp.lastName}`,
                        rowType: 'Sales Person',
                        level: emp.level,
                        ...mapApiMetrics(emp),
                    }
                }));
                return {
                    key: `${index}`,
                    data: {
                        manager: `${mgr.firstName} ${mgr.lastName}`,
                        salesPerson: '',
                        rowType: 'Manager',
                        level: mgr.totals ? mgr.totals.level : undefined,
                        ...mapApiMetrics(mgr.totals),
                    },
                    children,
                };
            });
        }

        // No manager/sales person detail from the API for this selection —
        // the TreeTable shows its default "no records" empty state.
        return [];
    }, [carlReportData]);

    const expandAllManagers = () => {
        const keys: any = {};
        managerTreeData.forEach((node) => { keys[node.key] = true; });
        setExpandedKeys(keys);
    };

    // Default view shows every manager's Sales Person rows expanded.
    useEffect(() => {
        expandAllManagers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [managerTreeData]);

    const allManagersExpanded = managerTreeData.some((node) => expandedKeys[node.key]);

    const toggleAllManagers = (e) => {
        e.stopPropagation();
        if (allManagersExpanded) {
            setExpandedKeys({});
        } else {
            expandAllManagers();
        }
    };

    const sellingDaysInfo = useMemo(() => {
        // Prefer the live Carl Report API's selling-day figures.
        if (carlReportData && carlReportData.sellingDays !== undefined) {
            return {
                sellingDays: carlReportData.sellingDays,
                daysPast: carlReportData.daysPast,
                daysRemaining: carlReportData.daysRemaining,
            };
        }
        return getSellingDaysInfo(selectedMonth, selectedYear);
    }, [carlReportData, selectedMonth, selectedYear]);

    const asOfDate = (selectedYear === currentYear && selectedMonth === currentMonth)
        ? moment().format('MM-DD-YYYY')
        : moment({ year: selectedYear, month: selectedMonth - 1 }).endOf('month').format('MM-DD-YYYY');

    const managerBodyTemplate = (rowData) => rowData.data.rowType === 'Manager' ? rowData.data.manager : '';
    const salesPersonBodyTemplate = (rowData) => rowData.data.rowType === 'Sales Person' ? rowData.data.salesPerson : '';
    const levelBodyTemplate = (rowData) => rowData.data.level;

    const togglerTemplate = (node, options) => {
        if (!node) {
            return;
        }
        const expanded = options.expanded;
        const iconClassName = classNames('p-treetable-toggler-icon pi pi-fw', {
            'pi-plus-circle': !expanded,
            'pi-minus-circle': expanded
        });
        return (
            <button type="button" className="treetablebuttonStyle p-treetable-toggler p-link" style={{ color: '#ffffff', backgroundColor: '#0065cb' }} tabIndex={-1} onClick={options.onClick}>
                <span className={iconClassName} aria-hidden="true"></span>
            </button>
        );
    };

    const managerHeaderTemplate = (
        <span>
            <button type="button" className="treetablebuttonStyle p-treetable-toggler p-link" style={{ color: '#ffffff', backgroundColor: '#0065cb' }} onClick={toggleAllManagers} title={allManagersExpanded ? 'Collapse all teams' : 'Expand all teams'}>
                <span className={classNames('p-treetable-toggler-icon pi pi-fw', { 'pi-plus-circle': !allManagersExpanded, 'pi-minus-circle': allManagersExpanded })} aria-hidden="true"></span>
            </button>
            Manager
        </span>
    );

    const rowClassName = (node) => {
        return { childRow: (node.key && node.key.includes('-')) };
    };

    const exportData = () => {
        const workbook = new ExcelJS.Workbook();
        const metricHeaders = metricColumns.map((c) => c.header);
        const mtdVsGoalIndex = metricColumns.findIndex((c) => c.field === 'mtdVsGoal');

        const locSheet = workbook.addWorksheet('Totals Table - Display');
        locSheet.addRow(['Location Name', ...metricHeaders]);
        locationTotalsData.forEach((row: any) => {
            const excelRow = locSheet.addRow([row.locationName, ...metricColumns.map((c) => row[c.field])]);
            fillMtdVsGoalCell(excelRow.getCell(mtdVsGoalIndex + 2), row.mtdVsGoal);
        });
        autoFitExcelColumns(locSheet);

        const detailSheet = workbook.addWorksheet('Details Table - Display');
        detailSheet.addRow(['Manager', 'Sales Person', 'Level', ...metricHeaders]);
        managerTreeData.forEach((mgr: any) => {
            const mgrRow = detailSheet.addRow([mgr.data.manager, mgr.data.salesPerson, mgr.data.level, ...metricColumns.map((c) => mgr.data[c.field])]);
            fillMtdVsGoalCell(mgrRow.getCell(mtdVsGoalIndex + 4), mgr.data.mtdVsGoal);
            (mgr.children || []).forEach((emp: any) => {
                const empRow = detailSheet.addRow([emp.data.manager, emp.data.salesPerson, emp.data.level, ...metricColumns.map((c) => emp.data[c.field])]);
                fillMtdVsGoalCell(empRow.getCell(mtdVsGoalIndex + 4), emp.data.mtdVsGoal);
            });
        });
        autoFitExcelColumns(detailSheet);

        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `CARL REPORT AS OF ${asOfDate}.xlsx`);
        });
    };

    // GET /reports/carlReport/pdf?month=&year=&primaryLocationId=
    const exportPdf = () => {
        const formData: any = {
            month: selectedMonth,
            year: selectedYear,
        };
        if (locationFilter) {
            formData.primaryLocationId = locationFilter;
        }
        if (conditionFilter.length) {
            formData.newUsed = conditionFilter.map((v) => v.toLowerCase()).join(',');
        }
        dispatch<any>(handlePrintCarlReport({
            formData,
            callback: (response) => {
                // clientPrintPdf opens the PDF in a new tab itself and resolves to
                // { data, error }; data is only set when the PDF was generated.
                if (response?.data) {
                    setcommonServerError(null);
                } else {
                    setcommonServerError('Failed to print the pdf');
                }
            }
        }));
    };

    return (

        isKarlProfileType ? <>
            <div className="reportFinanceProductWidth carlNewReport">
                <Stack styles={container} tokens={{ childrenGap: 10 }}>
                    <Stack
                        horizontal
                        verticalAlign="center"
                        horizontalAlign="space-between"
                        className="loc_btn_par_con"
                    >
                        <Row>
                            <Col className="alignHeaders">
                                <CustomFontText className="pageTitleFont pageName pageHeaderFont">{`CARL REPORT AS OF ${asOfDate}`}</CustomFontText>
                            </Col>
                        </Row>
                    </Stack>
                    {
                        commonServerError ?
                            <div>
                                <p className="genericErrorColor"><span>{commonServerError}</span></p>
                            </div> : null
                    }
                    <Stack
                        horizontal
                        verticalAlign="end"
                        horizontalAlign="space-between"
                        wrap
                        tokens={{ childrenGap: 20 }}
                        className="carlSummaryFilterRow"
                    >
                        <Stack horizontal tokens={{ childrenGap: 10 }}>
                            <div className="carlSummaryCard">
                                <div className="carlSummaryCardValue">{sellingDaysInfo.sellingDays}</div>
                                <div className="carlSummaryCardLabel">Selling Days</div>
                            </div>
                            <div className="carlSummaryCard">
                                <div className="carlSummaryCardValue">{sellingDaysInfo.daysPast}</div>
                                <div className="carlSummaryCardLabel">Days Past</div>
                            </div>
                            <div className="carlSummaryCard">
                                <div className="carlSummaryCardValue">{sellingDaysInfo.daysRemaining}</div>
                                <div className="carlSummaryCardLabel">Days Remaining</div>
                            </div>
                        </Stack>

                        <Stack horizontal tokens={{ childrenGap: 1 }}>
                            <div className="columnBtwSpacePad">
                                <label className="lbl-text">Select Month :</label>
                                <PRDropdown
                                    id="carlMonth"
                                    value={monthYearFilter}
                                    options={monthYearOptions}
                                    onChange={onMonthChange}
                                    optionLabel="text"
                                    optionValue="key"
                                    appendTo="self"
                                    className="custom-p-dropdown write-ups-location-dd"
                                    filter={false}
                                />
                            </div>
                            <div className="columnBtwSpacePad">
                                <label className="lbl-text">Deal Location :</label>
                                <PRDropdown
                                    id="carlLocation"
                                    value={locationFilter}
                                    options={locationList}
                                    onChange={onLocationChange}
                                    optionLabel="legalName"
                                    optionValue="id"
                                    appendTo="self"
                                    className="custom-p-dropdown write-ups-location-dd"
                                    filter={false}
                                />
                            </div>
                            <div className="columnBtwSpacePad">
                                <label className="lbl-text">New / Used :</label>
                                <MultiSelect
                                    id="carlCondition"
                                    value={conditionFilter}
                                    options={carlReportNewUsedOptions}
                                    onChange={onConditionChange}
                                    optionLabel="text"
                                    optionValue="key"
                                    appendTo="self"
                                    className="carlReportmultiselctPad carlReportmultiselect custom-p-dropdown write-ups-location-dd"
                                    maxSelectedLabels={1}
                                />
                            </div>
                        </Stack>
                    </Stack>
                        <Stack verticalAlign="end"
                        horizontalAlign="end" horizontal tokens={{ childrenGap: 10 }}>
                            <div>
                            <Link className="workbook-export-btn" color="inherit" onClick={exportData} data-pr-tooltip="XLS"  >
                                Export Excel
		                    </Link>
                        </div>
                        <div>
                            <Link style={{marginLeft: '10px'}} className="workbook-export-btn" color="inherit" onClick={exportPdf} data-pr-tooltip="PDF"  >
                                Export Pdf 
		                    </Link>
                        </div>
                            {/* <PrimaryButton className="carlExportBtn" onClick={exportData}>Export Data</PrimaryButton>
                            <PrimaryButton className="carlExportBtn" onClick={exportPdf}>Export PDF</PrimaryButton> */}
                        </Stack>

                    <div className="deskReport reportHeaderPart tablePadSizeWidth datatable-filter-demo trade-in-workboook dropDownBorderColor">
                        <DataTable
                            value={locationTotalsData}
                            className="p-datatable-customers"
                            paginator={false}
                            tableStyle={{ tableLayout: 'fixed', width: '100%' }}
                        >
                            <Column field="locationName" header="Location Name" style={{ width: '28.5%' }} sortable />
                            {metricColumns.map((col) => (
                                <Column
                                    key={col.field}
                                    field={col.field}
                                    header={col.header}
                                    style={{ width: metricColumnWidth(col.type) }}
                                    bodyClassName={(rowData) => mtdVsGoalCellClass(col, rowData[col.field])}
                                    sortable
                                    body={(rowData) => formatMetricValue(col.type, rowData[col.field])}
                                />
                            ))}
                        </DataTable>
                    </div>

                    <div className="datatable-filter-demo noMaxWidthTable cust-tree-tbl">
                        <div className="card reportsTableVal">
                            <TreeTable
                                value={managerTreeData}
                                selectionMode="single"
                                togglerTemplate={togglerTemplate}
                                rowClassName={rowClassName}
                                expandedKeys={expandedKeys}
                                onToggle={(e) => setExpandedKeys(e.value)}
                                tableStyle={{ tableLayout: 'fixed', width: '100%' }}
                            >
                                <Column field="manager" header={managerHeaderTemplate} expander body={managerBodyTemplate} bodyClassName="carlManagerCell" style={{ width: '14%' }} sortable />
                                <Column field="salesPerson" header="Sales Person" body={salesPersonBodyTemplate} style={{ width: '10.5%' }} sortable />
                                <Column field="level" header="Level" body={levelBodyTemplate} style={{ width: '4%' }} />
                                {metricColumns.map((col) => (
                                    <Column
                                        key={col.field}
                                        field={col.field}
                                        header={col.header}
                                        style={{ width: metricColumnWidth(col.type) }}
                                        bodyClassName={(node) => mtdVsGoalCellClass(col, (node?.data ?? node)?.[col.field])}
                                        sortable
                                        body={(rowData) => formatMetricValue(col.type, rowData.data[col.field])}
                                    />
                                ))}
                            </TreeTable>
                        </div>
                    </div>
                </Stack>
            </div>
        </> : null
    );
};
